import { FormProvider, useForm } from 'react-hook-form';
import { useSuspenseGetGraphneSimulationParameters } from '../hooks';
import { GanttSimulationParameters } from './GanttSimulationParameters';
import { GanttSimulationAvailability } from '../components/GanttSimulationAvailability';
import type { ApiSimulationParameterValue } from '@nexelem/graphne-api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { getParametersDefaultValues, getParametersSchema } from './schema';

type GanttSimulationCreateFormProps = {
  onSubmit: (data: ApiSimulationParameterValue[]) => Promise<void>;
  cancelButton: React.ReactNode;
  submitButtonLabel: string;
  headerTitle?: string;
};

export const GanttSimulationCreateForm = ({
  onSubmit,
  cancelButton,
  submitButtonLabel,
  headerTitle,
}: GanttSimulationCreateFormProps) => {
  const { data } = useSuspenseGetGraphneSimulationParameters();

  const [step, setStep] = useState<'PARAMETERS' | 'AVAILABILITY'>('PARAMETERS');

  const form = useForm({
    resolver: zodResolver(getParametersSchema(data)),
    defaultValues: getParametersDefaultValues(data),
  });

  const changeToAvailability = () => {
    setStep('AVAILABILITY');
  };

  const changeToParameters = () => {
    setStep('PARAMETERS');
  };

  const submit = async (data: Record<string, unknown>) => {
    const parameterValues = Object.entries(data).map(([key, value]) => {
      return {
        id: Number(key),
        value: value instanceof Date ? value.toISOString() : String(value).trim(),
      };
    });

    //** Async to handle form submitting - button loading state */
    await onSubmit(parameterValues).catch(() => {
      /** catch to disable button loading state */
    });
  };

  return (
    <FormProvider {...form}>
      <form className="h-full" onSubmit={form.handleSubmit(submit)}>
        {step === 'PARAMETERS' && (
          <GanttSimulationParameters
            cancelButton={cancelButton}
            headerTitle={headerTitle}
            submitButtonLabel={submitButtonLabel}
            onChangeStep={changeToAvailability}
          />
        )}
        {step === 'AVAILABILITY' && (
          <GanttSimulationAvailability
            cancelButton={cancelButton}
            submitButtonLabel={submitButtonLabel}
            onChangeStep={changeToParameters}
          />
        )}
      </form>
    </FormProvider>
  );
};
